import type { ReactNode } from "react";
import { Loader2, MoreHorizontal } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";

type Tone = "neutral" | "success" | "info" | "warning" | "danger";

const toneClasses: Record<Tone, string> = {
  neutral:
    "bg-slate-50 text-slate-700 ring-slate-200 dark:bg-slate-900/50 dark:text-slate-300 dark:ring-slate-800",
  success:
    "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-900",
  info:
    "bg-sky-50 text-sky-700 ring-sky-200 dark:bg-sky-950/40 dark:text-sky-300 dark:ring-sky-900",
  warning:
    "bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:ring-amber-900",
  danger:
    "bg-red-50 text-red-700 ring-red-200 dark:bg-red-950/40 dark:text-red-300 dark:ring-red-900",
};

export function AdminPage({
  eyebrow,
  title,
  description,
  actions,
  children,
  className,
}: {
  eyebrow?: string;
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("space-y-6", className)}>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          {eyebrow && (
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {eyebrow}
            </div>
          )}
          <h1 className="mt-1 text-2xl font-semibold tracking-tight text-foreground">
            {title}
          </h1>
          {description && (
            <div className="mt-1 max-w-3xl text-sm leading-6 text-muted-foreground">
              {description}
            </div>
          )}
        </div>
        {actions && <div className="flex shrink-0 flex-wrap gap-2">{actions}</div>}
      </div>
      {children}
    </div>
  );
}

export function AdminPanel({
  title,
  description,
  actions,
  children,
  className,
}: {
  title?: string;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={cn(
        "overflow-hidden rounded-xl border border-border bg-card text-card-foreground shadow-sm",
        className
      )}
    >
      {(title || description || actions) && (
        <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border px-4 py-3">
          <div className="min-w-0">
            {title && <h2 className="font-medium leading-6">{title}</h2>}
            {description && (
              <div className="mt-0.5 text-sm leading-5 text-muted-foreground">
                {description}
              </div>
            )}
          </div>
          {actions && <div className="flex shrink-0 flex-wrap gap-2">{actions}</div>}
        </div>
      )}
      <div>{children}</div>
    </section>
  );
}

export function AdminMetric({
  label,
  value,
  detail,
  tone = "neutral",
  to,
}: {
  label: string;
  value: ReactNode;
  detail?: ReactNode;
  tone?: Tone;
  to?: string;
}) {
  const content = (
    <div className="rounded-xl border border-border bg-card p-4 shadow-sm transition-colors hover:bg-muted/30">
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div
        className={cn(
          "mt-2 text-3xl font-semibold tracking-tight",
          tone === "success" && "text-emerald-700 dark:text-emerald-300",
          tone === "info" && "text-sky-700 dark:text-sky-300",
          tone === "warning" && "text-amber-700 dark:text-amber-300",
          tone === "danger" && "text-red-700 dark:text-red-300"
        )}
      >
        {value}
      </div>
      {detail && <div className="mt-1 text-xs text-muted-foreground">{detail}</div>}
    </div>
  );

  return to ? (
    <Link to={to} className="block">
      {content}
    </Link>
  ) : (
    content
  );
}

export function AdminStatusPill({
  children,
  tone = "neutral",
  className,
}: {
  children: ReactNode;
  tone?: Tone;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset",
        toneClasses[tone],
        className
      )}
    >
      {children}
    </span>
  );
}

export function adminToneForStatus(status: string | null | undefined): Tone {
  if (!status) return "neutral";
  if (["removed", "succeeded", "completed", "verified", "active", "done", "brand_registry_ready"].includes(status)) {
    return "success";
  }
  if (["submitted", "running", "scraping", "classifying", "discovering"].includes(status)) {
    return "info";
  }
  if (["flagged", "pending", "queued", "invite_sent", "watching", "needs_human", "unknown", "brand_registry_access_required", "brand_registry_access_pending"].includes(status)) {
    return "warning";
  }
  if (["rejected", "failed", "invalidated", "past_due"].includes(status)) {
    return "danger";
  }
  return "neutral";
}

export function AdminLoading({ label = "Loading" }: { label?: string }) {
  return (
    <div className="flex items-center gap-2 px-4 py-8 text-sm text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" />
      {label}
    </div>
  );
}

export function AdminEmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="px-4 py-10 text-center">
      <div className="font-medium text-foreground">{title}</div>
      {description && (
        <div className="mx-auto mt-1 max-w-lg text-sm leading-6 text-muted-foreground">
          {description}
        </div>
      )}
      {action && <div className="mt-4 flex justify-center">{action}</div>}
    </div>
  );
}

export function AdminActionMenu({
  label = "Actions",
  children,
}: {
  label?: string;
  children: ReactNode;
}) {
  return (
    <details className="relative">
      <summary className="inline-flex h-7 cursor-pointer list-none items-center gap-1 rounded-lg border border-border bg-background px-2.5 text-[0.8rem] font-medium hover:bg-muted [&::-webkit-details-marker]:hidden">
        <MoreHorizontal className="h-4 w-4" />
        {label}
      </summary>
      <div className="absolute right-0 z-20 mt-2 min-w-56 rounded-lg border border-border bg-popover p-1 text-popover-foreground shadow-lg">
        {children}
      </div>
    </details>
  );
}

export function AdminMenuItem({
  children,
  className,
  ...props
}: React.ComponentProps<"button">) {
  return (
    <button
      type="button"
      className={cn(
        "flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm hover:bg-muted disabled:pointer-events-none disabled:opacity-50",
        className
      )}
      {...props}
    >
      {children}
    </button>
  );
}
